import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { object, string } from 'yup';

import RightModalLayout from '../layouts/Modal/RightModalLayout';
import { SIDEBAR_ACTIONS } from '../../constants/actionsConstants';
import InputComponent from '../components/InputMC';
import DropdownMC from '../components/DropdownMC';
import FormProviderMC from '../components/FormProviderMC';
import CategoryModelMC from '../models/CategoryModelMC';
import SubcategoryModelMC from '../models/SubcategoryModelMC';

const CREATE_EDIT_VARIANT = {
  CREATE: 'CREATE',
  EDIT: 'EDIT'
};

const CreateEditCategory = ({
  selected,
  parentCategory,
  entityType = 'category',
  VARIANT = CREATE_EDIT_VARIANT.CREATE,
  refetch
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('CategoriesMC');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parentOptions, setParentOptions] = useState([]);

  const isCreateVariant = VARIANT === CREATE_EDIT_VARIANT.CREATE;
  const isSubcategory = entityType === 'subcategory';

  const titleKey = isSubcategory
    ? (isCreateVariant ? 'sidebar.title.createSubcategory' : 'sidebar.title.editSubcategory')
    : (isCreateVariant ? 'sidebar.title.createCategory' : 'sidebar.title.editCategory');

  const schema = object().shape({
    name: string()
      .required(t('sidebar.name_required'))
      .max(100, t('sidebar.name_max')),
    description: string().max(250, t('sidebar.description_max')),
    category_id: isSubcategory
      ? string().required(t('sidebar.parent_required'))
      : string().nullable()
  });

  const defaultValues = {
    name: selected?.name || '',
    description: selected?.description || '',
    category_id: selected?.category_id || parentCategory?.web_id || ''
  };

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues
  });

  useEffect(() => {
    if (!isSubcategory) return;

    CategoryModelMC.get()
      .then((response) => {
        const list = Array.isArray(response) ? response : [];
        setParentOptions(list.map((category) => ({
          id: category.web_id,
          label: category.name
        })));
      })
      .catch(() => {
        setParentOptions([]);
      });
  }, [isSubcategory]);

  useEffect(() => {
    methods.reset({
      name: selected?.name || '',
      description: selected?.description || '',
      category_id: selected?.category_id || parentCategory?.web_id || ''
    });
  }, [selected, parentCategory, methods]);

  const handleSubmit = async (data) => {
    setIsSubmitting(true);

    try {
      if (isSubcategory) {
        const payload = {
          subcategory: {
            name: data.name,
            description: data.description,
            category_id: data.category_id
          }
        };

        if (isCreateVariant) {
          const response = await SubcategoryModelMC.create(data.category_id, payload);
          showtoast(response.message || t('alerts.create_success'));
        } else {
          const parentWebId = selected.original_category_id || selected.category_id;
          const response = await SubcategoryModelMC.update(
            parentWebId,
            selected.web_id,
            payload
          );
          showtoast(response.message || t('alerts.update_success'));
        }
      } else {
        const payload = {
          category: {
            name: data.name,
            description: data.description
          }
        };

        if (isCreateVariant) {
          const response = await CategoryModelMC.save(payload);
          showtoast(response.message || t('alerts.create_success'));
        } else {
          const response = await CategoryModelMC.save({
            ...payload,
            id: selected.web_id
          });
          showtoast(response.message || t('alerts.update_success'));
        }
      }

      if (refetch) {
        refetch();
      }

      dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
    } catch (error) {
      showtoast(error.message || t('alerts.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    dispatch({ type: SIDEBAR_ACTIONS.CLOSE_SIDEBAR });
  };

  return (
    <div className="tw-wrapper">
      <RightModalLayout
        title={t(titleKey)}
        submitAction={methods.handleSubmit(handleSubmit)}
        cancelAction={handleCancel}
        submitLabel={isCreateVariant ? t('sidebar.save') : t('sidebar.update')}
        cancelLabel={t('sidebar.cancel')}
        submitDisabled={isSubmitting}
      >
        <FormProviderMC methods={methods} onSubmit={methods.handleSubmit(handleSubmit)}>
          <div className="tw-flex tw-flex-col tw-gap-4">
            <InputComponent
              id="name"
              name="name"
              label={t('sidebar.name_label')}
              useFloatingLabel
            />
            <InputComponent
              id="description"
              name="description"
              label={t('sidebar.description_label')}
              textarea
              maxLength={250}
              useFloatingLabel
            />
            {isSubcategory && (
              <DropdownMC
                id="category_id"
                name="category_id"
                label={t('sidebar.parent_label')}
                placeholder={t('sidebar.parent_placeholder')}
                options={parentOptions}
                defaultValue={defaultValues.category_id}
                search
                useFloatingLabel
              />
            )}
          </div>
        </FormProviderMC>
      </RightModalLayout>
    </div>
  );
};

export default CreateEditCategory;
